// @ts-nocheck
import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Autocomplete, TextField } from '@mui/material';

import { ReactComponent as DropdownArrow } from '../../../../assets/images/DropdownArrow.svg';
import { useAttributeListQuery } from '../../../../features/configuration/attributeLibrary/api/attributeQueries';
import { getErrorHelperText } from '../../../../utils/Utils';
import { EMPTY_ARRAY } from '../../../../const/CommonConst';
import { composeDraftBlurHandler } from '../../../../hooks/useDraftAutoSave.jsx';
import { DRAFT_INPUT_SX } from './TextFieldComponent';

const getAttributeListOptionLabel = (option) => option?.label || option?.name;
const isOptionEqualToValue = (option, value) =>
  String(option?.id) === String(value?.id);

const AttributeListFieldComponent = ({
  label,
  titleLabel,
  value,
  onChange,
  attribute,
  entityDetailsFormikForm,
  required = false,
  hasDraftChip = false,
  onDraftBlur,
}) => {
  /**
   * for AttributeList data type value will be
   * {"id": 876, "label":"attr type list"}
   */
  const { data: attributeList = EMPTY_ARRAY } = useAttributeListQuery();

  const selectedOption = useMemo(() => {
    return (
      attributeList.find(
        (option) => String(option?.id) === String(value?.id)
      ) || null
    );
  }, [attributeList, value]);

  const handleChange = useCallback(
    (_event, newValue) => {
      if (newValue) {
        onChange({ id: newValue?.id, label: newValue?.label });
      } else {
        onChange(null);
      }
    },
    [onChange]
  );

  const { onBlur, name } =
    entityDetailsFormikForm?.getFieldProps(attribute?.id) || {};

  const handleInputBlur = useCallback(
    (event) => {
      composeDraftBlurHandler(onBlur, onDraftBlur)(event);
    },
    [onBlur, onDraftBlur]
  );

  const handleClose = useCallback(() => {
    onDraftBlur?.();
  }, [onDraftBlur]);

  const renderInput = useCallback(
    (params) => (
      <TextField
        {...params}
        label={label}
        title={titleLabel}
        name={name}
        required={required}
        placeholder="Select attribute"
        error={Boolean(
          getErrorHelperText(
            entityDetailsFormikForm?.errors?.[attribute?.id],
            entityDetailsFormikForm?.touched?.[attribute?.id]
          )
        )}
        helperText={getErrorHelperText(
          entityDetailsFormikForm?.errors?.[attribute?.id],
          entityDetailsFormikForm?.touched?.[attribute?.id]
        )}
        sx={hasDraftChip ? DRAFT_INPUT_SX : undefined}
        slotProps={{
          ...params?.slotProps,
          input: {
            ...params?.slotProps?.input,
            placeholder: 'Select attribute',
          },

          inputLabel: {
            ...params?.slotProps?.inputLabel,
            shrink: true,
          },
        }}
      />
    ),
    [
      attribute?.id,
      entityDetailsFormikForm,
      label,
      required,
      titleLabel,
      hasDraftChip,
      name,
    ]
  );

  return (
    <Autocomplete
      value={selectedOption}
      options={attributeList}
      onChange={handleChange}
      onBlur={handleInputBlur}
      onClose={handleClose}
      noOptionsText="No attributes present."
      popupIcon={<DropdownArrow />}
      getOptionLabel={getAttributeListOptionLabel}
      isOptionEqualToValue={isOptionEqualToValue}
      renderInput={renderInput}
    />
  );
};

AttributeListFieldComponent.propTypes = {
  label: PropTypes.string.isRequired,
  titleLabel: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.shape({}),
  ]),
  onChange: PropTypes.func.isRequired,
  required: PropTypes.bool,
  attribute: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }).isRequired,
  entityDetailsFormikForm: PropTypes.shape({
    getFieldProps: PropTypes.func,
    setFieldTouched: PropTypes.func,
    errors: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.string])),
    touched: PropTypes.objectOf(PropTypes.bool),
  }),
  hasDraftChip: PropTypes.bool,
  onDraftBlur: PropTypes.func,
};

export default AttributeListFieldComponent;
